import { useEffect, useState } from "react";
import { Phone, Menu, X, MessageCircle } from "lucide-react";
import { BUSINESS, telLink, waLink } from "../lib/business";

const links = [
  { href: "#services", label: "خدماتنا" },
  { href: "#why", label: "لماذا نحن" },
  { href: "#reviews", label: "آراء العملاء" },
  { href: "#about", label: "من نحن" },
  { href: "#faq", label: "الأسئلة الشائعة" },
  { href: "#contact", label: "تواصل معنا" },
];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-navbar"
      className={`sticky top-0 z-50 transition-all duration-300 ${
        scrolled
          ? "backdrop-blur-xl bg-slate-950/85 border-b border-slate-800/60"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 h-16 lg:h-20 flex items-center justify-between">
        {/* Brand (right side for RTL - naturally first in DOM) */}
        <a
          href="#home"
          data-testid="brand-link"
          className="flex items-center gap-3 group"
        >
          <div className="relative w-10 h-10 lg:w-11 lg:h-11 rounded-xl bg-gradient-to-br from-cyan-500 to-cyan-700 flex items-center justify-center shadow-[0_0_20px_rgba(6,182,212,0.35)]">
            <span className="absolute inset-0 rounded-xl ring-1 ring-cyan-300/40" />
            <svg
              viewBox="0 0 24 24"
              className="w-5 h-5 lg:w-6 lg:h-6 text-white"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" />
            </svg>
          </div>
          <div className="leading-tight">
            <div className="text-sm lg:text-base font-bold text-white tracking-tight">
              {BUSINESS.nameBrand}
            </div>
            <div className="text-[11px] lg:text-xs text-cyan-400/80 font-medium">
              {BUSINESS.tagline}
            </div>
          </div>
        </a>

        {/* Desktop nav */}
        <nav className="hidden lg:flex items-center gap-8">
          {links.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-link-${l.href.replace("#", "")}`}
              className="text-sm text-slate-300 hover:text-cyan-400 transition-colors font-medium"
            >
              {l.label}
            </a>
          ))}
        </nav>

        {/* Desktop CTAs */}
        <div className="hidden lg:flex items-center gap-3">
          <a
            href={waLink("السلام عليكم، أود الاستفسار عن خدماتكم")}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="navbar-whatsapp-button"
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg border border-slate-700 hover:border-[#25D366]/60 text-slate-200 hover:text-[#25D366] transition-all text-sm font-medium"
          >
            <MessageCircle className="w-4 h-4" />
            واتساب
          </a>
          <a
            href={telLink}
            data-testid="navbar-call-button"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-cyan-500 hover:bg-cyan-400 text-slate-950 transition-all text-sm font-semibold shadow-[0_0_18px_rgba(6,182,212,0.35)]"
          >
            <Phone className="w-4 h-4" />
            اتصل الآن
          </a>
        </div>

        {/* Mobile toggle */}
        <button
          onClick={() => setOpen((v) => !v)}
          data-testid="mobile-menu-toggle"
          aria-label="فتح القائمة"
          className="lg:hidden w-10 h-10 flex items-center justify-center rounded-lg border border-slate-800 text-slate-200"
        >
          {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </div>

      {/* Mobile panel */}
      {open && (
        <div
          data-testid="mobile-menu-panel"
          className="lg:hidden border-t border-slate-800/60 bg-slate-950/95 backdrop-blur-xl"
        >
          <nav className="px-5 py-4 flex flex-col gap-1">
            {links.map((l) => (
              <a
                key={l.href}
                href={l.href}
                onClick={() => setOpen(false)}
                className="py-3 px-2 text-slate-200 hover:text-cyan-400 border-b border-slate-800/60 last:border-0"
              >
                {l.label}
              </a>
            ))}
            <div className="flex gap-3 mt-4">
              <a
                href={telLink}
                className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-3 rounded-lg bg-cyan-500 text-slate-950 font-semibold"
              >
                <Phone className="w-4 h-4" /> اتصل الآن
              </a>
              <a
                href={waLink()}
                target="_blank"
                rel="noopener noreferrer"
                className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-3 rounded-lg bg-[#25D366] text-white font-semibold"
              >
                <MessageCircle className="w-4 h-4" /> واتساب
              </a>
            </div>
            <div className="flex gap-3 mt-3">
              <a
                href={BUSINESS.socials.tiktok}
                target="_blank"
                rel="noopener noreferrer"
                className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg border border-slate-800 text-slate-200 text-sm font-medium"
              >
                <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4 text-fuchsia-400">
                  <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.8 20.1a6.34 6.34 0 0 0 10.86-4.43V8.94a8.16 8.16 0 0 0 4.77 1.52V7a4.85 4.85 0 0 1-1.84-.31z" />
                </svg>
                تيك توك
              </a>
              <a
                href={BUSINESS.socials.telegram}
                target="_blank"
                rel="noopener noreferrer"
                className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg border border-slate-800 text-slate-200 text-sm font-medium"
              >
                <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4 text-sky-400">
                  <path d="M9.78 18.65l.28-4.23 7.68-6.92c.34-.31-.07-.46-.52-.19L7.74 13.3 3.64 12c-.88-.25-.89-.86.2-1.3l15.97-6.16c.73-.33 1.43.18 1.15 1.3l-2.72 12.81c-.19.91-.74 1.13-1.5.71L12.6 16.3l-1.99 1.93c-.23.23-.42.42-.83.42z" />
                </svg>
                تيليجرام
              </a>
            </div>
          </nav>
        </div>
      )}
    </header>
  );
};

export default Navbar;
